import { fetchApi } from './api';

// ---- Types ----

export interface MsdsIngredient {
    name: string;
    casNumber: string;
    ecNumber?: string;
    reachNumber?: string;
    concentration: string;
    classification?: string;
}

export interface MsdsHStatement {
    code: string;
    text: string;
}

export interface MsdsExposureLimit {
    substance: string;
    casNumber: string;
    limitType: string;
    value: string;
    country: string;
}

export interface MsdsExtractedData {
    tradeName: string | null;
    usagePurpose: string | null;
    sdsVersion: string | null;
    sdsDate: string | null;
    originalSupplier: {
        name: string | null;
        street: string | null;
        city: string | null;
        country: string | null;
        phone: string | null;
        email: string | null;
    } | null;
    emergencyNumber: string | null;
    hStatements: MsdsHStatement[];
    ingredients: MsdsIngredient[];
    handlingNotes: string | null;
    exposureLimits: MsdsExposureLimit[];
    disposalInfo: string | null;
    transportInfo: {
        unNumber: string | null;
        properShippingName: string | null;
        hazardClass: string | null;
        packingGroup: string | null;
    } | null;
    legalReferences: string | null;
    physicalProperties: {
        flashPoint: string | null;
        boilingPoint: string | null;
        density: string | null;
        phValue: string | null;
        vocContent: string | null;
    } | null;
}

export interface MakBatValue {
    substance: string;
    casNumber: string;
    makValue: string | null;
    makUnit: string | null;
    batValue: string | null;
    batUnit: string | null;
    notation: string | null;
    source: 'suva-api' | 'static' | 'not-found';
}

export interface HStatementCheckResult {
    motherProtection: boolean;
    youthProtection: boolean;
    motherProtectionHCodes: string[];
    youthProtectionHCodes: string[];
    motherProtectionText: string | null;
    youthProtectionText: string | null;
}

export interface CoverpageFormData {
    coverpageDate: string;
    replacesVersionFrom?: string;
    tradeName: string;
    usagePurpose: string;
    sdsVersion: string;
    supplierName: string;
    supplierAddress: string;
    supplierPhone: string;
    supplierEmail: string;
    emergencyNumber: string;
    section7Notes: string;
    makBatValues: Array<{
        substance: string;
        casNumber: string;
        makValue: string | null;
        makUnit: string | null;
        notation: string | null;
    }>;
    section8PsaNotes: string;
    section13Notes: string;
    section15Notes: string;
    motherProtection: boolean;
    motherProtectionText: string;
    youthProtection: boolean;
    youthProtectionText: string;
    mengenschwelle: string;
}

export interface SavedCoverpage {
    Id: number;
    TradeName: string;
    SdsVersion: string | null;
    OriginalSupplier: string | null;
    Language: string;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedAt: string;
}

export interface SavedCoverpageDetail extends SavedCoverpage {
    FormData: CoverpageFormData;
}

// ---- API Client ----

export const msdsCoverpageApi = {
    extractPdf(pdfBase64: string, fileName?: string): Promise<{
        extracted: MsdsExtractedData;
        pdfTextLength: number;
        fileName: string;
    }> {
        return fetchApi('/msds-coverpage/extract', {
            method: 'POST',
            body: JSON.stringify({ pdfBase64, fileName }),
        });
    },

    lookupMak(ingredients: Array<{ casNumber: string; name: string }>): Promise<MakBatValue[]> {
        return fetchApi('/msds-coverpage/mak-lookup', {
            method: 'POST',
            body: JSON.stringify({ ingredients }),
        });
    },

    checkHStatements(hCodes: string[]): Promise<HStatementCheckResult> {
        return fetchApi('/msds-coverpage/h-check', {
            method: 'POST',
            body: JSON.stringify({ hCodes }),
        });
    },

    async generateDocument(formData: CoverpageFormData, format: 'docx' | 'pdf'): Promise<Blob> {
        const resp = await fetch('/api/v1/msds-coverpage/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ formData, format }),
        });

        if (!resp.ok) {
            const error = await resp.json().catch(() => ({ error: { message: 'Download failed' } }));
            throw new Error(error.error?.message || `Generation failed: ${resp.status}`);
        }

        return resp.blob();
    },

    listCoverpages(): Promise<SavedCoverpage[]> {
        return fetchApi('/msds-coverpage');
    },

    getCoverpage(id: number): Promise<SavedCoverpageDetail> {
        return fetchApi(`/msds-coverpage/${id}`);
    },

    saveCoverpage(data: {
        id?: number;
        tradeName: string;
        sdsVersion?: string;
        originalSupplier?: string;
        formData: CoverpageFormData;
        language?: string;
    }): Promise<{ id: number; isNew: boolean }> {
        return fetchApi('/msds-coverpage/save', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
};
